'use client';

import { useState, useCallback, useRef } from 'react';
import { BaseGame, GameStatus, GameResult, UseGameStateReturn } from '@/contexts/games/types';

/**
 * Универсальный хук для управления состоянием игры
 */
export function useGameState<T extends BaseGame>(
  initialGame: T,
  onGameComplete?: (result: GameResult) => void,
  onGameFail?: (result: GameResult) => void
): UseGameStateReturn<T> {
  const [game, setGame] = useState<T>(initialGame);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const gameRef = useRef<T>(initialGame);
  gameRef.current = game;

  // Обновление игры
  const updateGame = useCallback((updates: Partial<T>) => {
    setGame(prev => {
      const updated = { ...prev, ...updates, updatedAt: new Date() };
      gameRef.current = updated;
      return updated;
    });
    setError(null);
  }, []);

  // Сброс игры к начальному состоянию
  const resetGame = useCallback(() => {
    const resetGame: T = {
      ...initialGame,
      status: 'idle',
      createdAt: new Date(),
      updatedAt: new Date(),
      startedAt: undefined,
      completedAt: undefined,
      statistics: {
        ...initialGame.statistics,
        gamesPlayed: gameRef.current.statistics.gamesPlayed,
        gamesWon: gameRef.current.statistics.gamesWon,
        gamesLost: gameRef.current.statistics.gamesLost,
        totalTime: gameRef.current.statistics.totalTime,
        bestTime: gameRef.current.statistics.bestTime,
        averageTime: gameRef.current.statistics.averageTime,
        hintsUsed: gameRef.current.statistics.hintsUsed,
        currentStreak: gameRef.current.statistics.currentStreak,
        bestStreak: gameRef.current.statistics.bestStreak,
      },
    };
    
    setGame(resetGame);
    setError(null);
  }, [initialGame]);

  // Начало игры
  const startGame = useCallback(() => {
    const now = new Date();
    updateGame({
      status: 'playing' as GameStatus,
      startedAt: now,
      statistics: {
        ...gameRef.current.statistics,
        gamesPlayed: gameRef.current.statistics.gamesPlayed + 1,
      },
    } as Partial<T>);
  }, [updateGame]);

  // Пауза игры
  const pauseGame = useCallback(() => {
    if (gameRef.current.status === 'playing') {
      updateGame({ status: 'paused' as GameStatus } as Partial<T>);
    }
  }, [updateGame]);

  // Возобновление игры
  const resumeGame = useCallback(() => {
    if (gameRef.current.status === 'paused') {
      updateGame({ status: 'playing' as GameStatus } as Partial<T>);
    }
  }, [updateGame]);

  // Завершение игры (победа)
  const completeGame = useCallback((result?: Partial<GameResult>) => {
    const now = new Date();
    const gameTime = gameRef.current.startedAt 
      ? now.getTime() - gameRef.current.startedAt.getTime()
      : 0;

    const currentStats = gameRef.current.statistics;
    const newGamesWon = currentStats.gamesWon + 1;
    const newTotalTime = currentStats.totalTime + gameTime;
    const newAverageTime = newTotalTime / newGamesWon;
    const newBestTime = currentStats.bestTime 
      ? Math.min(currentStats.bestTime, gameTime)
      : gameTime;
    const newStreak = currentStats.currentStreak + 1;
    const newBestStreak = Math.max(currentStats.bestStreak, newStreak);

    updateGame({
      status: 'completed' as GameStatus,
      completedAt: now,
      statistics: {
        ...currentStats,
        gamesWon: newGamesWon,
        totalTime: newTotalTime,
        averageTime: newAverageTime,
        bestTime: newBestTime,
        currentStreak: newStreak,
        bestStreak: newBestStreak,
      },
    } as Partial<T>);

    // Создаем результат игры
    const gameResult: GameResult = {
      gameId: gameRef.current.id,
      status: 'won',
      time: gameTime,
      difficulty: gameRef.current.difficulty,
      hintsUsed: 0, // Будет переопределено в конкретной игре
      completedAt: now,
      ...result,
    };

    onGameComplete?.(gameResult);
  }, [updateGame, onGameComplete]);

  // Провал игры
  const failGame = useCallback((reason?: string) => {
    const now = new Date();
    const gameTime = gameRef.current.startedAt 
      ? now.getTime() - gameRef.current.startedAt.getTime()
      : 0;

    const currentStats = gameRef.current.statistics;
    const newGamesLost = currentStats.gamesLost + 1;
    const newTotalTime = currentStats.totalTime + gameTime;

    updateGame({
      status: 'failed' as GameStatus,
      completedAt: now,
      statistics: {
        ...currentStats,
        gamesLost: newGamesLost,
        totalTime: newTotalTime,
        currentStreak: 0, // Сбрасываем текущую серию
      },
    } as Partial<T>);

    // Создаем результат игры
    const gameResult: GameResult = {
      gameId: gameRef.current.id,
      status: 'lost',
      time: gameTime,
      difficulty: gameRef.current.difficulty,
      hintsUsed: 0, // Будет переопределено в конкретной игре
      completedAt: now,
      metadata: reason ? { reason } : undefined,
    };

    onGameFail?.(gameResult);
    setError(reason || 'Game failed');
  }, [updateGame, onGameFail]);

  return {
    game,
    isLoading,
    error,
    updateGame,
    resetGame,
    startGame,
    pauseGame,
    resumeGame,
    completeGame,
    failGame,
  };
}
